import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from '@/components/Button';
import FormInput from '@/components/FormInput';
import FormPassword from '@/components/FormPassword';
import useApi from '@/hooks/useApi';
import { HOME_ROUTE } from '@/config';
import { getPasswordRules } from '@/utils/validation';
import ConsentCheckbox from '@/pages/Auth/ConsentCheckbox';
import styles from './AcceptForm.module.scss';

interface AcceptFormFields {
  name: string;
  password: string;
}

interface AcceptFormProps {
  companyName: string;
  inviterName: string;
}

const AcceptForm = ({ companyName, inviterName }: AcceptFormProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'common', 'invite']);
  const [consentGiven, setConsentGiven] = useState(false);

  const [acceptInvitation, { loading, response }] = useApi(
    'organization.invitation.accept'
  );

  const id = searchParams.get('invitationId') ?? '';
  const code = searchParams.get('code') ?? '';

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<AcceptFormFields>({
    defaultValues: { name: '', password: '' },
  });

  const acceptInvitationHandler: SubmitHandler<AcceptFormFields> = (values) => {
    if (!consentGiven) {
      return;
    }

    void acceptInvitation({
      code,
      id,
      name: values.name,
      password: values.password,
    });
  };

  const handleConsentToggle = () => {
    setConsentGiven((prevState) => !prevState);
  };

  useEffect(() => {
    if (response && response.success) {
      localStorage.setItem('token', response.token);
      // Team members join an org that already has its own subscription, so we land
      // them on the dashboard. Phone/KYC verification, if required for an action,
      // are surfaced contextually by the require-account-ready middleware.
      void navigate(HOME_ROUTE, { replace: true });
    }
  }, [navigate, response]);

  return (
    <>
      <div className={styles.paragraph}>
        {t('invite:form.paragraph1', {
          companyName,
          inviterName,
        })}
      </div>
      <div className={styles.paragraph}>{t('invite:form.paragraph2')}</div>
      <form
        onSubmit={(event) => void handleSubmit(acceptInvitationHandler)(event)}
        className={styles.form}
        noValidate
      >
        <FormInput
          errorMessage={errors.name?.message}
          id="name"
          label={t('invite:form.name')}
          {...register('name', {
            required: t('invite:form.nameRequired'),
          })}
        />
        <FormPassword
          autoComplete="new-password"
          id="password"
          label={t('auth:password.newPassword')}
          placeholder={t('auth:password.newPasswordPlaceholder')}
          withRequirements="newPassword"
          errorMessage={errors.password?.message}
          {...register('password', getPasswordRules(t))}
        />
        <ConsentCheckbox
          checked={consentGiven}
          onToggle={handleConsentToggle}
        />
        <Button
          className={styles.button}
          submit
          loading={loading}
          disabled={!consentGiven}
        >
          {t('invite:form.button')}
        </Button>
      </form>
    </>
  );
};

export default AcceptForm;
